import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSidebar } from "@/contexts/SidebarContext";
import {
  CompanyIcon,
  UsersManagementIcon,
  PermissionsIcon,
  MonitoringIcon,
  ProceduresIcon,
  AssetsIcon,
  ReliabilityIcon,
  DownloadAppIcon,
  ControlPanelIcon,
} from "@/components/Icons/index";
import { SearchIcon } from "@/components/Icons/SearchIcon";
import { ChevronDoubleRightIcon } from "@/components/Icons/ChevronDoubleRightIcon";
import { ChevronDoubleLeftIcon } from "@/components/Icons/ChevronDoubleLeftIcon";
import { TextField } from "@/components/Input";

interface NavigationItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  children?: NavigationItem[];
}

const adminNavigationItems: NavigationItem[] = [
  {
    name: "dashboard",
    path: "/admin",
    icon: <MonitoringIcon className="w-5 h-5" />,
  },
  {
    name: "company",
    path: "/admin/tenants",
    icon: <CompanyIcon className="w-5 h-5" />,
  },
  {
    name: "users_management",
    path: "/admin/users-management",
    icon: <UsersManagementIcon className="w-5 h-5" />,
    children: [
      {
        name: "users",
        path: "/admin/users",
        icon: <UsersManagementIcon className="w-4 h-4" />,
      },
      {
        name: "roles",
        path: "/admin/roles",
        icon: <PermissionsIcon className="w-4 h-4" />,
      },
      {
        name: "permissions",
        path: "/admin/permissions",
        icon: <PermissionsIcon className="w-4 h-4" />,
      },
    ],
  },
  {
    name: "monitoring",
    path: "/admin/monitoring",
    icon: <MonitoringIcon className="w-5 h-5" />,
  },
  {
    name: "procedures",
    path: "/admin/procedures",
    icon: <ProceduresIcon className="w-5 h-5" />,
  },
  {
    name: "assets",
    path: "/admin/assets",
    icon: <AssetsIcon className="w-5 h-5" />,
  },
  {
    name: "reliability",
    path: "/admin/reliability",
    icon: <ReliabilityIcon className="w-5 h-5" />,
  },
  {
    name: "download_app",
    path: "/admin/download-app",
    icon: <DownloadAppIcon className="w-5 h-5" />,
  },
  {
    name: "control_panel",
    path: "/admin/control-panel",
    icon: <ControlPanelIcon className="w-5 h-5" />,
  },
];
export const AdminSidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, toggleSidebar, isMobileOpen, closeMobileSidebar } =
    useSidebar();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const toggleExpanded = (itemName: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName]
    );
  };

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  // Filter navigation items based on search query
  const filteredNavigationItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return adminNavigationItems;
    }

    const query = searchQuery.toLowerCase();
    return adminNavigationItems.filter((item) => {
      const itemName = t(`admin.navigation.${item.name}`).toLowerCase();
      const hasMatchingName = itemName.includes(query);

      // Check if any child matches
      const hasMatchingChild = item.children?.some((child) => {
        const childName = t(`admin.navigation.${child.name}`).toLowerCase();
        return childName.includes(query);
      });

      return hasMatchingName || hasMatchingChild;
    });
  }, [searchQuery, t]);

  const renderNavigationItem = (item: NavigationItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.name);
    const active = isActive(item.path);

    return (
      <div key={item.name}>
        <div
          className={`flex items-center gap-2 text-sm font-normal h-[44px] cursor-pointer transition-all duration-300 ease-in-out ${
            level === 0 ? "pl-4 pr-3 py-3" : "p-3 pl-10"
          } ${isCollapsed ? "justify-center" : ""} ${
            active
              ? "bg-[#000000]/24 text-white"
              : "text-[#CAD5E2] hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20"
          }`}
          onClick={() => {
            if (hasChildren && !isCollapsed) {
              toggleExpanded(item.name);
            } else if (!hasChildren) {
              navigate(item.path);
              closeMobileSidebar();
            }
          }}
        >
          <span className="flex-shrink-0">{item.icon}</span>
          {!isCollapsed && (
            <>
              <span className="flex-1">
                {t(`admin.navigation.${item.name}`)}
              </span>
              {hasChildren && (
                <ChevronDoubleRightIcon
                  className={`w-4 h-4 transition-transform duration-300 ease-in-out ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                />
              )}
            </>
          )}
        </div>

        {hasChildren && isExpanded && !isCollapsed && (
          <div>
            {item.children!.map((child) => (
              <div
                key={child.name}
                onClick={() => {
                  navigate(child.path);
                  closeMobileSidebar();
                }}
                className={`flex items-center gap-2 text-sm font-normal h-[44px] pl-10 pr-3 cursor-pointer transition-all duration-300 ease-in-out ${
                  isActive(child.path)
                    ? "bg-[#000000]/24 text-white"
                    : "text-[#CAD5E2] hover:text-white hover:bg-white/10 border border-transparent hover:border-white/20"
                }`}
              >
                <span className="flex-shrink-0">{child.icon}</span>
                <span>{t(`admin.navigation.${child.name}`)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity duration-500 ease-in-out ${
          isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobileSidebar}
      />

      <div
        className={`sidebar admin-sidebar fixed left-0 z-40 top-16 h-[calc(100vh-4rem)] transition-[transform,width] duration-500 ease-in-out ${
          isCollapsed ? "w-14" : "w-[250px]"
        } max-lg:-translate-x-full max-lg:top-0 max-lg:h-screen max-lg:transition-transform max-lg:duration-500 max-lg:ease-in-out ${
          isMobileOpen ? "max-lg:translate-x-0" : ""
        }`}
        style={{
          backgroundColor: "#182836",
          backgroundImage: "none",
          backdropFilter: "none",
        }}
      >
        <div className="flex flex-col h-full">
          {/* Search and Toggle Button */}
          <div className="px-2 py-1.5 border-b border-white/20 flex items-center gap-2">
            {!isCollapsed ? (
              <div className="flex-1">
                <TextField
                  type="text"
                  placeholder={t("admin.navigation.search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startIcon={<SearchIcon className="w-4 h-4" />}
                  variant="filled"
                  size="sm"
                  fullWidth
                />
              </div>
            ) : (
              <button
                onClick={toggleSidebar}
                className="rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all p-1.5"
                title="Expand to search"
              >
                <SearchIcon className="w-4 h-4" />
              </button>
            )}
            {!isCollapsed && (
              <button
                onClick={toggleSidebar}
                className="rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all p-1.5 flex-shrink-0"
                title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <ChevronDoubleRightIcon className="w-4 h-4" />
                ) : (
                  <ChevronDoubleLeftIcon className="w-4 h-4" />
                )}
              </button>
            )}
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto">
            {filteredNavigationItems.length > 0 ? (
              filteredNavigationItems.map((item) => renderNavigationItem(item))
            ) : (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">
                {t("admin.navigation.no_results")}
              </div>
            )}
          </nav>

          {/* Bottom Section */}
          <div className="p-4 border-t border-white/20">
            {!isCollapsed && (
              <div className="text-xs text-gray-400 text-center">
                <div className="gradient-text font-semibold">DS Insight</div>
                <div className="text-gray-500">Admin Panel v1.0</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
